/*
 * WHO A NAME IS, from what the map already carries.
 *
 * Tapping a circle or choosing a search result opens this — the figure's kind, the texts that name
 * it and how often, and whether the cross-scripture identity is agreed or disputed. It is built
 * from the node's own fields, so it needs no fetch; the passages themselves (and the "read it
 * elsewhere" links) are a heavier feature that arrives with verse loading. It is a labelled region
 * with a real close control, so a keyboard user can open it, read it, and leave it.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { PASSAGES_ID, VersesList } from '../verses/VersesList';
import { ReportsList } from '../reports/ReportsList';
import type { ReportsListProps } from '../reports/ReportsList';
import { filterPassages } from '../verses/filterPassages';
import { sortPassages } from '../verses/sortPassages';
import type { VersesListProps } from '../verses/VersesList';
import { useLang } from '../i18n/lang';
import type { Strings } from '../i18n/strings';
import { OUR_BAR, barWidth, largestCount, spread } from './named-in';
import type { Trail } from './trails';

export interface PanelNode {
  id: string;
  name: string;
  type: string;
  arabic?: string | null;
  /** Who this is in one phrase — "Companion; the most prolific narrator of hadith". */
  role?: string | null;
  agreement?: string;
  note?: string | null;
  counts: Record<string, number>;
  corpora: string[];
  auto?: boolean;
  /**
   * Every spelling the texts use for this figure, by the spelling table it was declared in.
   *
   * How the panel can say that the Hebrew Bible calls him Enoch and the Quran calls him Idris —
   * which is most of what a reader wants from a figure two scriptures share under two names.
   */
  forms?: Record<string, string[]>;
}

export interface PanelText {
  id: string;
  name: string;
  colour: string;
  /** Which spelling tables this text was searched with — how `forms` is read back per text. */
  spellingScopes?: readonly string[];
}

/**
 * WHAT THIS TEXT CALLS THE FIGURE, where that is not simply the name at the top of the panel.
 *
 * The PRIMARY spelling for the text's own table — the first, which is the one the gazetteer
 * declares as the name rather than a variant of it. Taking the first that merely DIFFERS from the
 * headline was the obvious rule and the wrong one: the Hebrew Bible's table for Noah holds
 * `["Noah", "Noe"]`, and it would answer "Noe", offering a reader the Douay spelling as though it
 * were what the King James says.
 *
 * Null when the text calls them what the panel already does, or does not name them at all. A row
 * reading "Hebrew Bible · Adam" under a panel headed "Adam" is noise on every figure; an empty
 * table means the text describes the figure without naming them, which the panel says elsewhere
 * and better.
 *
 * Compared against the WHOLE headline, never against a part of it. A merged figure is titled
 * "Enoch / Idris" precisely because two texts call him two things — suppressing a spelling for
 * appearing somewhere in that title would blank the one case this exists for.
 */
export function nameInText(node: PanelNode, text: PanelText): string | null {
  const forms = node.forms;
  if (!forms) return null;
  const headline = node.name.toLowerCase();
  for (const scope of text.spellingScopes ?? []) {
    const primary = (forms[scope] ?? [])[0];
    if (primary && primary.toLowerCase() !== headline) return primary;
  }
  return null;
}

export interface NodePanelProps {
  node: PanelNode;
  texts: PanelText[];
  onClose: () => void;
  /** The figure's passages, loaded on demand. */
  verses?: VersesListProps;
  /** Travel to another name a verse mentions — passed through to the passage list. */
  onJump?: (id: string) => void;
  /** Open a verse into the passage around it — the expanded reader. */
  onExpand?: (id: number) => void;
  /**
   * Other figures that answer to this name, resolved to names by the caller.
   *
   * The bundle carries ids (`shares`); the panel does no lookups of its own, the same way it
   * takes `reports` already loaded. Resolved in App, which holds every node.
   */
  sharesWith?: { id: string; name: string }[];
  /**
   * Identity claims naming this figure — the OPPOSITE of `sharesWith`.
   *
   * `sharesWith` says: one spelling, two different figures, and the reader should not mistake
   * them for one. This says: different names, and someone holds them to be one being — while
   * someone else denies it. Both sides are carried, and the panel shows both, because the
   * circles stay separate either way and the reader is the one deciding.
   */
  identities?: {
    id: string;
    claim: string;
    dispute: PanelDispute;
    others: { id: string; name: string }[];
  }[];
  /**
   * A dispute about THIS figure's own identity — the merged case.
   *
   * Enoch and Idris are one circle, and whether they are one man is what is argued. Rendered by
   * the same component as the claim above, deliberately: a reader should not be able to tell
   * from the presentation whether a curator merged two names or kept them apart, because that
   * choice is a judgement and showing it as one would make it read as a verdict.
   */
  dispute?: PanelDispute;
  /**
   * Arguments that turn on PASSAGES rather than on this figure's identity.
   *
   * "Did God rest after creating, and can God tire?" is reached from God and from the Sabbath,
   * because that is where a reader who cares about it already is — not from an index of disputes,
   * which would be a menu, and this app has none. Same DisputeBlock as everything else; what is
   * extra is the passages, which open in the reader.
   */
  passageDisputes?: {
    id: string;
    dispute: PanelDispute;
    passages: readonly string[];
  }[];
  /* They open through `onOpenRef` below, which the reports already use for the same job. */
  /** The figure's shared passages — the same episode told in the Bible and the Quran. */
  stories?: { id: string; bibleRange: string; quranRange: string }[];
  /** Open one shared passage, read side by side. */
  onOpenStory?: (id: string) => void;
  /** Where this figure's account differs between the Bible and the Quran — each a question, opened as both. */
  divergences?: { question: string; bibleRange: string; quranRange: string }[];
  /** Open the divergence at this index, read side by side. */
  onOpenDivergence?: (index: number) => void;
  /** Open the figure's discussion in its own side-by-side drawer. */
  onOpenDiscussion?: () => void;
  /** Open a discussion for a single verse, by its citation. */
  onDiscussVerse?: (ref: string) => void;
  /** Step back to the previously-viewed figure, and whether there is one. */
  onBack?: () => void;
  canGoBack?: boolean;
  /** The figure's voice room — a slot, so the panel needn't know the social client. */
  social?: React.ReactNode;
  /**
   * Hadith that name this figure — "what is also related about this person".
   *
   * Passed in already loaded, like `verses`, because the panel does no fetching of its own.
   * The bundle carries only a COUNT per figure; the reports live in their own artifact and
   * arrive when a panel opens, so a reader who opens no figure never downloads 8.2 MB of
   * Arabic. Absent means either none exist or they have not arrived yet — the count says
   * which.
   */
  reports?: ReportsListProps;
  /** Open an ayah a report quotes, by citation — see ReportsList. */
  onOpenRef?: (ref: string) => void;
  /**
   * Narrow the map to one scripture, from the NAMED IN row that names it.
   *
   * Theirs does this (engine.js:2198) and it is the better placement: a reader looking at "Adam is
   * named in all five" is already asking which of the five, and the filter bar is at the far edge of
   * the screen. The panel row IS the question, so it is where the answer should be reachable.
   *
   * Absent means the rows are not clickable, which is what a caller with no map wants.
   */
  onOnlyCorpus?: (corpusId: string) => void;
  /**
   * Where a reader can go next from this figure — see trails.ts.
   *
   * Built by the caller, like `sharesWith` and `identities`, because it needs the tie graph and
   * every node's ring and the panel holds neither. Travel goes through `onJump`, the same door a
   * name in a verse and a name in a report already use.
   */
  trails?: readonly Trail[];
}

/**
 * WHAT EACH KIND IS CALLED, and it must cover every kind the data can carry.
 *
 * A kind with no entry here falls through to `type.unknown`, so a node the pipeline typed
 * correctly would be labelled "unknown" on the page — silently, because a fallback cannot tell
 * "we do not know" from "nobody wrote the label". The map has gained two kinds' worth of nodes
 * recently (scriptures, observances) and would have gained the defect with them.
 *
 * Exported so a test can hold it against ENTITY_KINDS in both directions.
 */
/** A question and who answers it how — the shape both a merged and a split dispute arrive in. */
export interface PanelDispute {
  question: string;
  /** Readonly to match the domain type it arrives as — see schema/types.ts Dispute. */
  positions: readonly { held: string; says: string }[];
}

/**
 * ONE DISPUTE, RENDERED THE SAME WAY WHEREVER IT COMES FROM.
 *
 * The merged case (Enoch/Idris is one circle whose identity is argued) and the split case
 * (YHWH and Allah are two circles someone holds to be one) share this component on purpose.
 * Whether a curator merged or split is a judgement about that entity, and if the two looked
 * different on screen the reader would read the layout as the app's own verdict.
 *
 * POSITIONS ARE UNRANKED AND ALL OF THEM RENDER. No "held" side and no "disputed" side — those
 * words made whoever came third into the opposition. Each position names WHO holds it, because
 * "some say" is how a curator's own view gets laundered into scholarship.
 */
function DisputeBlock(
  { dispute, lead, names, onJump, refs, onOpenRef }: {
    dispute: PanelDispute;
    /** The claim being made, where there is one to state above the question. */
    lead?: string;
    /** Other circles this claim gathers, as jumps. */
    names?: { id: string; name: string }[];
    onJump?: (id: string) => void;
    /** Passages the argument turns on, where it turns on passages rather than names. */
    refs?: readonly string[];
    onOpenRef?: (ref: string) => void;
  },
): React.ReactElement {
  /*
   * `dir="auto"` ON EVERY PIECE OF CURATED PROSE, because it is English inside a panel that may
   * be Hebrew or Arabic.
   *
   * A screenshot in Hebrew showed "?Is the Quranic Idris the biblical Enoch" — the question mark
   * thrown to the far left, because the paragraph inherits the page's RTL direction and neutral
   * punctuation goes to the end of the LINE rather than the end of the sentence. The CSS was
   * already direction-aware; the text was not.
   *
   * `auto` rather than `ltr` so a translated claim flips of its own accord: the browser reads the
   * first strong character and follows it, which is right whichever language the prose ends up in.
   */
  return (
    <details className="panel-identity">
      <summary>
        <span className="panel-identity-claim" dir="auto">{lead ?? dispute.question}</span>
        {(names ?? []).map(other => (
          <button
            key={other.id}
            type="button"
            className="panel-shares-name"
            onClick={event => { event.preventDefault(); onJump?.(other.id); }}
            disabled={!onJump}
          >
            {other.name}
          </button>
        ))}
        {/* The passages, as the same pills the names use — both go somewhere when tapped. */}
        {(refs ?? []).map(ref => (
          <button
            key={ref}
            type="button"
            className="panel-shares-name"
            onClick={event => { event.preventDefault(); onOpenRef?.(ref); }}
            disabled={!onOpenRef}
          >
            {ref}
          </button>
        ))}
      </summary>
      {/* When a claim was stated above, the question still has to be asked — the claim is one
          party's answer to it, and leading with only the claim would make it the default. */}
      {lead && <p className="panel-identity-question" dir="auto">{dispute.question}</p>}
      {dispute.positions.map((position, i) => (
        <p className="panel-identity-side" dir="auto" key={`${position.held}-${i}`}>
          <span className="panel-identity-who">{position.held}</span>
          {position.says}
        </p>
      ))}
    </details>
  );
}

export const TYPE_KEY: Record<string, keyof Strings> = {
  person: 'type.person',
  place: 'type.place',
  people: 'type.people',
  divine: 'type.divine',
  unknown: 'type.unknown',
  object: 'type.object',
  observance: 'type.observance',
  institution: 'type.institution',
  scripture: 'type.scripture',
  angelic: 'type.angelic',
  deity: 'type.deity',
  time: 'type.time',
  language: 'type.language',
  practice: 'type.practice',
  ruling: 'type.ruling',
  event: 'type.event',
  realm: 'type.realm',
  attribute: 'type.attribute',
  religion: 'type.religion',
  tribe: 'type.tribe',
  celestial: 'type.celestial',
  concept: 'type.concept',
  parable: 'type.parable',
  prayer: 'type.prayer',
  creature: 'type.creature',
};

export function NodePanel(
  { node, texts, onClose, verses, onJump, onExpand, sharesWith, identities, dispute, passageDisputes, stories, onOpenStory, divergences, onOpenDivergence, onOpenDiscussion, onDiscussVerse, onBack, canGoBack, social, reports, onOpenRef, onOnlyCorpus, trails }: NodePanelProps,
): React.JSX.Element {
  const { t } = useLang();
  const byId = new Map(texts.map(text => [text.id, text]));
  const named = node.corpora
    .map(id => ({ text: byId.get(id), count: node.counts[id] ?? 0 }))
    .filter((entry): entry is { text: PanelText; count: number } => entry.text !== undefined);
  /* Some figures carry no linked verses at all: those the scriptures describe without ever naming
     (the son Abraham was told to sacrifice; Pharaoh's wife), and designations too broad to tag verse
     by verse (an epithet like "the Most High", which the build deliberately keeps out of matching so
     it does not falsely mark hundreds of verses). Such a node still earns its place — it holds a
     dispute or an unnamed figure — but "Named in" and "Passages" would be empty headings that read
     as broken. When nothing is linked, we say so plainly instead. */
  const linked = named.length > 0;

  /*
   * THE VERSES ARE THE LAST LONG SECTION, and this panel opens with six shorter ones before them.
   * Measured on a 900px screen: the passages start 1,708px down, where theirs reaches them at 339px
   * behind a tab.
   *
   * THE ORDER IS NOT THE DEFECT — leading with what is argued over is this library's whole thesis,
   * and every section below carries a note saying why it sits where it does. What was missing is any
   * way DOWN. So this adds a jump and moves nothing: a reader who came for the scripture gets there
   * in one press, and a reader working through the panel still meets the sections in the order they
   * were put in.
   *
   * Scoped to this panel's own subtree rather than the document, because the id belongs to a child
   * that a second panel would also render.
   */
  const root = useRef<HTMLElement>(null);
  const toPassages = useCallback(() => {
    root.current?.querySelector(`#${PASSAGES_ID}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);
  /* The widest bar on this panel, which every other bar is drawn as a share of — see named-in.ts for
     why a share and not the flat per-passage rate theirs uses. */
  const widest = largestCount(named.map(entry => entry.count));
  /* How far the name reaches, out of every scripture the build carries — not out of however many
     happen to be listed on this panel, which would make every figure "named in all". */
  const reach = spread(named.length, texts.length);

  /*
   * AND THERE IS A SECOND REASON A FIGURE HAS NO PASSAGES, which the first message got wrong.
   *
   * "The scriptures point to this figure by description" is true of the Queen of Sheba and
   * Potiphar's wife, and false of Abu Hurayra — the scriptures do not point to him at all. He
   * is a 7th-century Companion who is in neither the Bible nor the Quran, and 48 nodes are now
   * like him: the Companions, Khadija, al-Khandaq, the Medinan tribes. Every one of them was
   * being told a reader that scripture describes them, which it does not.
   *
   * The panel can tell the two apart from what it already holds: a figure with no passages but
   * with reports is not described-but-unnamed, they are attested somewhere else in the library.
   * No new field — the distinction is exactly the presence of the thing that made them a node.
   */
  const onlyInReports = !linked && (reports?.reports.length ?? 0) > 0;

  /* Filtering a busy figure's passages down to what the reader wants — a free-text query over text
     and citation, and per-scripture chips. Reset per node by the key App gives this component. */
  const [query, setQuery] = useState('');
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  /* Which scripture leads the list — '' keeps the canonical order (see sortPassages). */
  const [lead, setLead] = useState('');
  const toggleCorpus = (id: string): void => {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allPassages = verses?.passages ?? [];
  const shown = useMemo(
    () => sortPassages(filterPassages(allPassages, query, hidden), lead || null),
    [allPassages, query, hidden, lead],
  );
  const filtering = query.trim() !== '' || hidden.size > 0;

  /*
   * `data-map-chrome` BECAUSE THIS COVERS THE MAP — the largest thing that does, and the last to say
   * so. Every other control declares it where the control is written (`MapHint`,
   * `MapOrientation`, the overlay). This did not, so both renderers went on placing names in the
   * half of the canvas the panel sits over, counted them as placed, and the figures that could have
   * been named there lost their turn to writing nobody can read.
   *
   * It surfaced through the open name's fan: a citation was reported as drawn and could not be
   * found anywhere on the screen, because it had been written underneath this.
   */
  return (
    <aside ref={root} className="panel" role="region" data-map-chrome aria-label={t('panel.about', { name: node.name })}>
      <button type="button" className="panel-close" onClick={onClose} aria-label={t('action.close')}>×</button>
      {canGoBack && onBack && (
        <button type="button" className="panel-back" onClick={onBack}>
          <span aria-hidden="true">←</span> {t('action.back')}
        </button>
      )}
      <h2 className="panel-name">{node.name}</h2>
      {/*
        * THE ARABIC, WHEN IT IS A DIFFERENT NAME — and for many figures it is the same one.
        *
        * Adam is Adam in both, and the panel printed him twice: the title, and then the identical
        * string again in the corner, which reads as a rendering that failed rather than as a name.
        *
        * Counted rather than guessed: 76 of the 1,099 carry an `arabic` at all, and 8 of those are
        * the same word as the `name` — Adam, Hud, Salih, Sarah, Haman, Shuʿayb among them. Compared
        * after trimming, because the two come from different stages and one may carry a stray space.
        */}
      {node.arabic !== null && node.arabic !== undefined && node.arabic.trim() !== node.name.trim() && (
        <p className="panel-arabic" lang="ar" dir="rtl">{node.arabic}</p>
      )}
      <p className="panel-kind">{t(TYPE_KEY[node.type] ?? 'type.unknown')}</p>
      {/*
        * WHO THIS IS, IN A PHRASE — curated, and until now never shown to anybody.
        *
        * 153 figures carry a `role` and the panel has never rendered it. "Person" followed by
        * a paragraph is a slow way to answer "who am I looking at"; "Person · Companion; the
        * most prolific narrator of hadith" answers it at a glance, and the note still says the
        * rest underneath.
        *
        * It matters most exactly where the reader knows least. A hadith-only Companion has no
        * passages, no translation and no alias — this line and the reports are the whole of
        * what the page can tell them.
        */}
      {node.role && <p className="panel-role">{node.role}</p>}

      {/*
        * ANOTHER FIGURE ANSWERS TO THIS NAME — computed since the beginning, shown until now
        * by nothing.
        *
        * `build-graph` works this out deliberately and its own comment says why: "Both
        * entities are then flagged as sharing the name, so the interface can say so rather
        * than hide it." The interface did not say so. 21 figures carry the flag and a reader
        * met none of it.
        *
        * These are the collisions that matter most here. The KJV renders Joshua as "Jesus"
        * twice; Saul is the king of Israel and the man who became Paul; Judah the patriarch
        * and Judas Iscariot are one word in Greek. A reader who lands on one and is not told
        * about the other has been handed a resolved ambiguity that the texts do not resolve —
        * which is the single thing this project is most careful never to do.
        */}
      {sharesWith && sharesWith.length > 0 && (
        <p className="panel-shares">
          <span className="panel-shares-label">{t('panel.sharesName')}</span>
          {sharesWith.map(other => (
            <button
              key={other.id}
              type="button"
              className="panel-shares-name"
              onClick={() => onJump?.(other.id)}
              disabled={!onJump}
            >
              {other.name}
            </button>
          ))}
        </p>
      )}

      {linked && (
        <>
          {/* The sentence before the list, because "named in all five" is the thing a reader wants and
              counting rows to get it is work. Theirs leads with the same line (engine.js:2200). */}
          <p className="panel-reach" dir="auto">
            {t(reach === 'all' ? 'panel.reachAll' : reach === 'only' ? 'panel.reachOnly' : 'panel.reachSome', {
              name: node.name,
              named: named.length,
              total: texts.length,
              text: named[0]?.text.name ?? '',
            })}
          </p>
          <div className="panel-heading-row">
            {/* THE SPACE IS LOAD-BEARING — a heading and the control beside it are siblings, so this
                read back as "Named inPassages ↓". Added in the same session that fixed the same
                thing in the map's live corner, which is the point: it is not a bug in one place. */}
            <h3 className="panel-heading">{t('panel.namedIn')}</h3>{' '}
            {/* Shown only when there is something to land on — VersesList renders on `linked && verses`,
                and `linked` IS this list being non-empty, so `verses` is the whole of the gate. */}
            {verses !== undefined && (
              <button type="button" className="panel-jump" onClick={toPassages}>
                {/* The word is the heading's own, so the jump cannot come to say something else. */}
                {t('verses.heading')}<span aria-hidden="true"> ↓</span>
              </button>
            )}
          </div>
          <ul className="panel-texts">
            {named.map(({ text, count }) => {
              /* What this text calls them, when that is not the name already at the top. */
              const calls = nameInText(node, text);
              const row = (
                <>
                  <span className="panel-swatch" style={{ backgroundColor: text.colour }} aria-hidden="true" />
                  <span className="panel-text-name">
                    {text.name}
                    {calls && <span className="panel-calls"> · {calls}</span>}
                  </span>{' '}
                  {/* The bar carries no text of its own — the count beside it already says the number,
                      and a reader using a screen reader gets that rather than a length in pixels. */}
                  <span
                    className="panel-bar"
                    style={{ width: `${barWidth(count, widest, OUR_BAR)}px`, backgroundColor: text.colour }}
                    aria-hidden="true"
                  />
                  <span className="panel-count">{count} {t(count === 1 ? 'unit.passage' : 'unit.passages')}</span>
                </>
              );
              return (
                <li key={text.id}>
                  {onOnlyCorpus
                    ? (
                      <button
                        type="button"
                        className="panel-text-row panel-text-row--act"
                        onClick={() => onOnlyCorpus(text.id)}
                        title={t('panel.onlyThis', { text: text.name })}
                      >
                        {row}
                      </button>
                    )
                    : <span className="panel-text-row">{row}</span>}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/*
        * NAMES SOMEONE HOLDS TO BE ONE BEING.
        *
        * The claim is always visible; the argument is behind a disclosure, because heldBy and
        * disputedBy run to several sentences each and four of these can name one circle — Elohim
        * carries three. A reader who wants the short answer gets it in the summary line and the
        * names beside it; a reader who wants to know WHO says so opens it.
        *
        * Both sides always render. A claim with only its supporters shown is the merge this
        * whole mechanism exists to avoid, wearing a disclosure triangle.
        */}
      {passageDisputes && passageDisputes.length > 0 && (
        <div className="panel-identities">
          <span className="panel-identities-label">{t('panel.argued')}</span>
          {passageDisputes.map(argument => (
            <DisputeBlock
              key={argument.id}
              dispute={argument.dispute}
              refs={argument.passages}
              onOpenRef={onOpenRef}
            />
          ))}
        </div>
      )}

      {identities && identities.length > 0 && (
        <div className="panel-identities">
          <span className="panel-identities-label">{t('panel.identity')}</span>
          {identities.map(claim => (
            <DisputeBlock
              key={claim.id}
              dispute={claim.dispute}
              lead={claim.claim}
              names={claim.others}
              onJump={onJump}
            />
          ))}
        </div>
      )}

      {(onOpenDiscussion || social) && (
        <div className="panel-community">
          {onOpenDiscussion && (
            <button type="button" className="panel-discuss" onClick={onOpenDiscussion}>
              {t('panel.openDiscussion')}
            </button>
          )}
          {social}
        </div>
      )}

      {/* The disputed callout wears the map's gold ring and halo, so the figure you tapped BECAUSE it was
          ringed in gold tells the same story in the drawer — and the open question that drew you in is the
          loudest thing here, not a grey footnote. When contested, the note explains the dispute, so it
          lives inside the callout; otherwise it reads as a plain note below. */}
      {/* THE MERGED DISPUTE GOES HERE, not in a block of its own.
          A first attempt put it up beside `shares` and the same argument then appeared twice —
          once as attributed positions and again as the note's prose below. This callout was
          already the right home: it is about THIS figure's identity, which is what a merged
          dispute is about, and it carries the weight the question deserves. The note stays as
          the fallback for the 21 contested rows whose positions nobody has separated yet. */}
      {node.agreement === 'contested' && (
        <div className="panel-disputed" role="note">
          <p className="panel-disputed-flag">{t('panel.contested')}</p>
          {dispute
            ? <DisputeBlock dispute={dispute} />
            : node.note && <p className="panel-disputed-note">{node.note}</p>}
        </div>
      )}
      {/*
        * HOW A NAME WAS FOUND IS NOT A READER'S BUSINESS — Ariel, 2026-08-22: *"do not expose to
        * user that a node was found automatically, this is irrelevant for the user"*.
        *
        * This drew "Found automatically by reading the texts — not checked by hand." under 1,568 of
        * the 2,409 figures. It described the PIPELINE, and a reader who wants to know whether a
        * claim is checked is served by what the panel says about the claim — the contested flag
        * above, and the note, which says in its own words when nothing is being claimed about a
        * name's kind.
        *
        * `node.auto` itself stays: the map still draws these at half strength, which is the same
        * honesty expressed where it costs the reader nothing to read.
        */}
      {node.agreement !== 'contested' && node.note && <p className="panel-note">{node.note}</p>}

      {stories && stories.length > 0 && (
        <div className="panel-shared">
          <h3 className="panel-heading">{t('panel.shared')}</h3>
          <p className="panel-shared-lead">{t('panel.sharedLead')}</p>
          <ul className="panel-shared-list">
            {stories.map(story => (
              <li key={story.id}>
                <button type="button" className="panel-shared-open" onClick={() => onOpenStory?.(story.id)}>
                  {story.bibleRange} <span aria-hidden="true">↔</span> {story.quranRange}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {divergences && divergences.length > 0 && (
        <div className="panel-shared">
          <h3 className="panel-heading">{t('compare.differ')}</h3>
          <p className="panel-shared-lead">{t('panel.differLead')}</p>
          <ul className="panel-shared-list">
            {divergences.map((divergence, i) => (
              <li key={`${divergence.bibleRange}|${divergence.quranRange}`}>
                <button type="button" className="panel-differ-open" onClick={() => onOpenDivergence?.(i)}>
                  <span className="panel-differ-q">{divergence.question}</span>
                  <span className="panel-differ-ranges">{divergence.bibleRange} <span aria-hidden="true">↔</span> {divergence.quranRange}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {verses && verses.status === 'ready' && allPassages.length > 0 && (
        <div className="verse-controls">
          <input
            type="search"
            className="verse-search"
            placeholder={t('panel.filterPlaceholder')}
            value={query}
            onChange={event => setQuery(event.target.value)}
            aria-label={t('panel.filterLabel', { name: node.name })}
          />
          {named.length > 1 && (
            <div className="verse-chips" role="group" aria-label={t('panel.chipsLabel')}>
              {named.map(({ text }) => {
                const off = hidden.has(text.id);
                return (
                  <button
                    key={text.id}
                    type="button"
                    className={off ? 'verse-chip verse-chip--off' : 'verse-chip'}
                    aria-pressed={!off}
                    onClick={() => toggleCorpus(text.id)}
                  >
                    <span className="verse-chip-swatch" style={{ backgroundColor: text.colour }} aria-hidden="true" />
                    {text.name}
                  </button>
                );
              })}
            </div>
          )}
          {named.length > 1 && (
            <label className="verse-sort">
              <span className="verse-sort-label">{t('panel.sortLabel')}</span>
              <select className="verse-sort-select" value={lead} onChange={event => setLead(event.target.value)}>
                <option value="">{t('panel.sortInOrder')}</option>
                {named.map(({ text }) => (
                  <option key={text.id} value={text.id}>{t('panel.sortLeadFirst', { name: text.name })}</option>
                ))}
              </select>
            </label>
          )}
          {filtering && (
            <p className="verse-count" role="status">
              {shown.length === 0 ? t('panel.noMatch') : t('panel.showing', { shown: shown.length, total: allPassages.length })}
            </p>
          )}
        </div>
      )}

      {/*
        * ABOVE THE PASSAGES, NOT AFTER THEM.
        *
        * Under its own heading, because hadith are not scripture and must not read as a
        * fifth column beside the Quran — ROADMAP is explicit that the interface would be
        * asserting an equivalence Muslim readers do not hold, with no prose doing it.
        *
        * But "own heading" was first written as "append last", and a screenshot showed what
        * that means: Abraham has 340 published passages, so the section landed below all of
        * them and no reader would ever reach it. Placed as a sibling section before the
        * list, it is reachable; the heading keeps it distinct. It is also collapsed by
        * default inside ReportsList's own markup, so it announces itself without pushing
        * the passages off the screen.
        */}
      {/* onJump is the passages' own handler: a name reached through a report and a name
          reached through a verse must travel the same way, or the map has two doors. */}
      {/*
        * `failed` KEEPS THE SECTION, and this gate is the second of two that dropped it.
        *
        * `useNodeReports` distinguishes "this figure has no hadith" from "we could not reach them",
        * and says in its own header why that matters: the bundle already told the reader there were
        * some. Both callers then keyed on the row count — App deciding whether to pass the prop, and
        * this deciding whether to render it — so a failed fetch removed the whole section from a
        * figure the bundle says has 1,599 reports, twice over and independently.
        */}
      {reports && (reports.reports.length > 0 || reports.failed) && (
        <ReportsList {...reports} onOpenRef={onOpenRef} onJump={onJump} />
      )}

      {linked
        ? verses && <VersesList {...verses} passages={shown} onJump={onJump} onExpand={onExpand} onDiscuss={onDiscussVerse} lead={byId.get(lead)?.name ?? null} />
        : <p className="panel-empty">{t(onlyInReports ? 'panel.noPassagesReportsOnly' : 'panel.noPassages')}</p>}

      {/*
        * WHERE TO GO NEXT, at the foot of the panel where a reader arrives having finished reading.
        *
        * Theirs closes every panel this way (engine.js:2258) and it is what stops a panel being a
        * terminus: without it, "who is this connected to" is answerable only by closing the drawer
        * and hunting the map for a line. Each chip says which way it goes — a ring nearer the centre
        * or further out — because that is the one thing the name alone cannot tell you.
        */}
      {trails && trails.length > 0 && (
        <div className="panel-trails">
          <h3 className="panel-heading">{t('panel.trails')}</h3>
          <div className="panel-trail-row">
            {trails.map(trail => (
              <button
                type="button"
                className="panel-trail"
                key={trail.id}
                onClick={() => onJump?.(trail.id)}
                /* The citation is the whole reason the cited trails are offered first, so it is said
                   rather than merely ranked on. */
                title={trail.ref === null ? undefined : t('panel.linkedBy', { ref: trail.ref })}
              >
                <span
                  className={trail.glyph === 'square' ? 'panel-trail-mark panel-trail-mark--place' : 'panel-trail-mark'}
                  style={{ backgroundColor: trail.colour }}
                  aria-hidden="true"
                />
                <span>{trail.name}</span>
                {trail.direction !== '' && (
                  <span className="panel-trail-dir">{t(trail.direction === 'inward' ? 'panel.inward' : 'panel.outward')}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
